import { useState } from 'react';
import {
	Box,
	FormHelperText,
	IconButton,
	InputAdornment,
	OutlinedInput,
	Typography
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Our Components
import PropTypes from 'prop-types';
import CommonPropTypes from 'shared/prop-types';
import { PASSWORD } from 'components/Input/Types';

import { isPercentInput, isPriceInput, getInputType } from './isValidTextInput';

const StyledNumberInput = styled(OutlinedInput)`
	input[type='number'] {
		-moz-appearance: textfield;
	}

	input[type='number']::-webkit-inner-spin-button,
	input[type='number']::-webkit-outer-spin-button {
		-webkit-appearance: none;
	}
`;

// TODO @Daniel see if we can keep state within an input field and possible toggle debouncing the values when needed as an opt-in
function TextInput({
	defaultValue,
	disabled,
	error,
	helperText,
	inputProps,
	label,
	labelSx,
	onBlur,
	onChange,
	placeholder,
	required,
	subLabel,
	sx,
	type,
	value,
	withVisibleToggle,
	defaultVisability
}) {
	const [visible, setVisible] = useState(defaultVisability);

	const [wasChecked, setWasChecked] = useState(false);

	// TODO @Daniel if type is tel then modify the onChange behavior
	return (
		<Box sx={sx}>
			{label && (
				<Typography
					variant="subtitle1"
					sx={{ marginBottom: 1, ...labelSx }}
				>
					{label}
				</Typography>
			)}
			<StyledNumberInput
				// This check is if its visible return the type passed in props
				// Otherwise set the type to password that way the toggle behaves as intended
				disabled={disabled}
				type={visible ? getInputType(type) : PASSWORD}
				error={wasChecked && error}
				placeholder={placeholder}
				value={value}
				defaultValue={defaultValue}
				onBlur={() => {
					if (wasChecked === false) setWasChecked(true);
					onBlur();
				}}
				onChange={(e) => onChange(e.target.value)}
				startAdornment={
					isPriceInput(type) && (
						<InputAdornment position="start">$</InputAdornment>
					)
				}
				endAdornment={
					(withVisibleToggle && (
						<InputAdornment position="end">
							<IconButton
								aria-label="toggle password visibility"
								onClick={() => setVisible(!visible)}
								onMouseDown={(e) => e.preventDefault()}
								edge="end"
							>
								{!visible ? <VisibilityOff /> : <Visibility />}
							</IconButton>
						</InputAdornment>
					)) ||
					(isPercentInput(type) && (
						<InputAdornment position="end">%</InputAdornment>
					))
				}
				fullWidth
				required={required}
				inputProps={inputProps}
			/>
			{/* Below may be complex but if checked. There is an error. And we provided helper test display it! */}
			{!subLabel && error && wasChecked && helperText && (
				<FormHelperText
					variant="standard"
					sx={{
						marginLeft: 2
					}}
				>
					{helperText}
				</FormHelperText>
			)}
			{/* TODO @Daniel figure out a way to rework this component to support errorText and helperText so they're separate */}
			{subLabel && (
				<FormHelperText
					variant="standard"
					sx={{
						marginLeft: 2
					}}
				>
					{subLabel}
				</FormHelperText>
			)}
		</Box>
	);
}

TextInput.propTypes = {
	disabled: PropTypes.bool,
	error: PropTypes.bool,
	sx: CommonPropTypes.sx,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
	labelSx: CommonPropTypes.sx,
	helperText: PropTypes.string,
	placeholder: PropTypes.string,
	onChange: PropTypes.func,
	onBlur: PropTypes.func,
	type: PropTypes.oneOf([
		'email',
		'number',
		'price',
		'percent',
		'tel',
		'text',
		'password',
		'url',
		'visible'
	]),
	defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	required: PropTypes.bool,
	withVisibleToggle: PropTypes.bool,
	defaultVisability: PropTypes.bool
};

TextInput.defaultProps = {
	disabled: false,
	error: false,
	sx: {},
	label: undefined,
	labelSx: {},
	helperText: undefined,
	placeholder: undefined,
	onChange: () => {},
	onBlur: () => undefined,
	type: undefined,
	defaultValue: undefined,
	required: false,
	withVisibleToggle: false,
	defaultVisability: true
};

export default TextInput;
